import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { churchInfo } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone, Mail, Eye, EyeOff, ChevronRight, User } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [authType, setAuthType] = useState('phone');
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pin: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Register with basic info - profile will be completed on next page
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: authType === 'email' ? formData.email : '',
        phone: authType === 'phone' ? formData.phone : '',
        pin: authType === 'phone' ? formData.pin : '',
        password: authType === 'email' ? formData.password : '',
        profileComplete: false
      });
      // Navigate to Create Profile page after successful registration
      navigate('/create-profile');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Church Logo */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-20 h-20 rounded-full bg-white p-1 shadow-lg mb-3">
            <img
              src={churchInfo.logo}
              alt={churchInfo.name}
              className="w-full h-full object-contain rounded-full"
            />
          </div>
        </div>

        {/* Registration Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-6">
          <h2 className="text-xl font-bold text-navy-900 text-center mb-1">Create Account</h2>
          <p className="text-sm text-gray-500 text-center mb-6">Join our church family</p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Tab Buttons */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => setAuthType('phone')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                authType === 'phone'
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Phone className="w-4 h-4" />
              Phone + PIN
            </button>
            <button
              onClick={() => setAuthType('email')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                authType === 'email'
                  ? 'bg-white text-navy-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="space-y-2">
              <Label className="text-navy-900 font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-tan-600" />
                Full Name
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  name="firstName"
                  placeholder="First Name"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <Input
                  name="lastName"
                  placeholder="Last Name"
                  className="h-11 bg-gray-50 border-gray-200 rounded-lg"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {authType === 'phone' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-navy-900 font-medium">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(559) 555-1234"
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pin" className="text-navy-900 font-medium">Create PIN (4-6 digits)</Label>
                  <div className="relative">
                    <Input
                      id="pin"
                      name="pin"
                      type={showPin ? 'text' : 'password'}
                      placeholder="Enter a PIN"
                      className="h-12 bg-gray-50 border-gray-200 rounded-lg pr-10"
                      value={formData.pin}
                      onChange={handleChange}
                      required
                      minLength={4}
                      maxLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPin(!showPin)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-navy-900 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@email.com"
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-navy-900 font-medium">Create Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter a password"
                      className="h-12 bg-gray-50 border-gray-200 rounded-lg pr-10"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white font-medium rounded-lg text-base"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Continue'} 
              {!loading && <ChevronRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-tan-600 hover:text-tan-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
